import { HStack, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Button, Modal, Text } from '@pimm/base';
import { useMutateDisableEnable } from '../hooks/mutate-disable-enable.hook';

export interface EnableDisableSensor {
  equipmentId?: string;
  instanceId?: string;
  isEnabled?: boolean;
  isDoor?: boolean;
  sensorName?: string;
}

type SensorSettingConfirmationProps = {
  isOpen: boolean;
  sensor?: EnableDisableSensor;
  siteId?: string;
  onClose: () => void;
  onSuccess?: (label: string, oos: boolean) => void;
};

export const SensorSettingConfirmation = ({ isOpen, sensor, siteId, onClose, onSuccess }: SensorSettingConfirmationProps) => {
  const { t } = useTranslation();

  const isDoor = sensor?.isDoor;
  const isEnabled = sensor?.isEnabled;
  const isDisabling = !!isEnabled;

  const changeStatus = useMutateDisableEnable({
    onSuccess: () => {
      if (sensor?.sensorName) onSuccess?.(sensor.sensorName, isDisabling);
      onClose();
    },
  });

  const handleConfirm = () => {
    if (siteId && sensor?.instanceId && sensor?.equipmentId) {
      changeStatus.mutate({
        isEnabled: sensor.isEnabled,
        InstanceId: sensor.instanceId,
        EquipmentId: sensor.equipmentId,
        siteId,
      });
    }
  };

  const title = isDisabling
    ? isDoor
      ? t('store_equipment:disable_door_sensor')
      : t('store_equipment:disable_temperature_sensor')
    : isDoor
    ? t('store_equipment:enable_door_sensor')
    : t('store_equipment:enable_temperature_sensor');

  const confirmLabel = isDisabling ? t('store_equipment:disable_sensor') : t('store_equipment:enable_sensor');

  const bodyText = isDisabling
    ? isDoor
      ? t('store_equipment:disable_sensor_warning_door')
      : t('store_equipment:disable_sensor_warning_temperature')
    : isDoor
    ? t('store_equipment:enable_sensor_message_door')
    : t('store_equipment:enable_sensor_message_temperature');

  return (
    <Modal isOpen={isOpen} onClose={onClose} _content={{ rounded: 'xl', p: 0, w: 400 }}>
      <VStack space={0}>
        <VStack px={5} pt={5} pb={2} space={0}>
          <Text size="xl" fontWeight={700} color="gray.900" pr={8}>
            {title}
          </Text>
          {!!sensor?.sensorName && (
            <Text size="md" color="gray.500" fontWeight={500}>
              {sensor.sensorName}
            </Text>
          )}
        </VStack>

        <VStack px={5} pb={4} space={3}>
          {isDisabling && (
            <Text size="md" fontWeight={700} color="error.600">
              {t('store_equipment:warning')}:
            </Text>
          )}
          <Text size="lg" color="gray.800">
            {bodyText}
          </Text>
          {isDisabling && (
            <Text size="lg" color="gray.600" fontStyle="italic">
              {t('store_equipment:disable_sensor_reminder')}
            </Text>
          )}
          {changeStatus.isError && (
            <Text size="sm" color="error.600" fontWeight={500}>
              {t(changeStatus.is503Error ? 'store_equipment:service_busy_retry' : 'store_equipment:sorry_message')}
            </Text>
          )}
        </VStack>

        <HStack space={2} px={5} pb={5} justifyContent="flex-end">
          <Button alternate outline bg="white" minH={9} disabled={changeStatus?.isLoading} onPress={onClose}>
            {t('store_equipment:cancel')}
          </Button>
          <Button
            isLoading={changeStatus?.isLoading}
            minH={9}
            bg={isDisabling ? 'error.600' : 'success.600'}
            borderColor={isDisabling ? 'error.600' : 'success.600'}
            _hover={{ bg: isDisabling ? 'error.800' : 'success.800' }}
            _pressed={{ bg: isDisabling ? 'error.800' : 'success.800' }}
            _text={{ color: 'white' }}
            onPress={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </HStack>
      </VStack>
    </Modal>
  );
};
